import { supabase } from '../lib/supabase';

const OTP_EXPIRY_MINUTES = 10;

// Generate a 6-digit OTP
const generateOTP = () => Math.floor(100000 + Math.random() * 900000).toString();

// Build the OTP email HTML
const buildOTPEmailHtml = (otp, fullName) => `
  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #2563eb; margin: 0;">Studyexpo</h1>
      <p style="color: #64748b; margin-top: 8px;">Virtual Education Fair Platform</p>
    </div>
    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; border-radius: 12px; text-align: center; margin-bottom: 30px;">
      <h2 style="color: white; margin: 0 0 10px 0;">Your Verification Code</h2>
      <div style="background: white; display: inline-block; padding: 20px 40px; border-radius: 8px; margin-top: 20px;">
        <p style="font-size: 36px; font-weight: bold; color: #2563eb; margin: 0; letter-spacing: 8px;">${otp}</p>
      </div>
    </div>
    <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
      <p style="margin: 0 0 10px 0; color: #334155;">Hi ${fullName || 'there'},</p>
      <p style="margin: 0 0 10px 0; color: #334155;">Thank you for registering with Studyexpo! Please use the verification code above to complete your registration.</p>
      <p style="margin: 0; color: #334155;"><strong>This code will expire in ${OTP_EXPIRY_MINUTES} minutes.</strong></p>
    </div>
    <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; border-radius: 4px; margin-bottom: 20px;">
      <p style="margin: 0; color: #92400e; font-size: 14px;">
        <strong>Security Note:</strong> Never share this code with anyone. Studyexpo staff will never ask for your verification code.
      </p>
    </div>
    <div style="text-align: center; padding-top: 20px; border-top: 1px solid #e2e8f0;">
      <p style="color: #64748b; font-size: 14px; margin: 0;">If you didn't request this code, please ignore this email.</p>
      <p style="color: #64748b; font-size: 12px; margin-top: 10px;">© 2026 Studyexpo. All rights reserved.</p>
    </div>
  </div>
`;

// Send email via Edge Function (non-blocking helper)
const sendEmailViaEdgeFunction = async (to, subject, html) => {
  const supabaseUrl = (import.meta.env?.VITE_SUPABASE_URL || '').replace(/\/$/, '');
  const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-email`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

  try {
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ to, subject, html, type: 'otp_verification' }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    console.log('✅ [OTP] Email sent via Edge Function:', data);
    return true;
  } catch (err) {
    clearTimeout(timeoutId);
    console.warn('⚠️ [OTP] Edge Function email failed (non-blocking):', err?.message);
    return false;
  }
};

export const otpService = {
  // Store OTP in Supabase so it survives page refreshes
  async sendEmailOTP(email, fullName) {
    console.log('🔵 [OTP] Sending email OTP to:', email);

    const otp = generateOTP();
    const expiresAt = new Date(Date.now() + OTP_EXPIRY_MINUTES * 60 * 1000).toISOString();

    // Upsert OTP into otp_verifications table
    const { error: dbError } = await supabase
      .from('otp_verifications')
      .upsert(
        {
          identifier: email.toLowerCase().trim(),
          type: 'email',
          otp_code: otp,
          expires_at: expiresAt,
          verified: false,
          attempts: 0,
        },
        { onConflict: 'identifier,type' }
      );

    if (dbError) {
      console.error('❌ [OTP] DB upsert failed:', dbError);
      // Fall back to localStorage if DB fails
      const fallbackKey = `otp_fallback_${email.toLowerCase().trim()}`;
      localStorage.setItem(fallbackKey, JSON.stringify({ otp, expiresAt, attempts: 0 }));
      console.log('⚠️ [OTP] Using localStorage fallback, OTP:', otp);
    } else {
      console.log('✅ [OTP] OTP stored in DB:', otp);
    }

    // Send email in background — do NOT await, do NOT block registration
    const emailHtml = buildOTPEmailHtml(otp, fullName);
    sendEmailViaEdgeFunction(
      email,
      `Your Studyexpo Verification Code: ${otp}`,
      emailHtml
    ).then(sent => {
      if (!sent) {
        console.warn('⚠️ [OTP] Email delivery failed. OTP is still valid in DB.');
      }
    });

    return { success: true, message: 'OTP generated. Check your email.' };
  },

  async sendMobileOTP(mobile, fullName) {
    console.log('🔵 [OTP] Sending mobile OTP to:', mobile);

    const normalizedMobile = mobile.replace(/\D/g, '').replace(/^(\+91|91)/, '').slice(-10);
    const otp = generateOTP();
    const expiresAt = new Date(Date.now() + OTP_EXPIRY_MINUTES * 60 * 1000).toISOString();

    // Store in DB
    const { error: dbError } = await supabase
      .from('otp_verifications')
      .upsert(
        {
          identifier: normalizedMobile,
          type: 'mobile',
          otp_code: otp,
          expires_at: expiresAt,
          verified: false,
          attempts: 0,
        },
        { onConflict: 'identifier,type' }
      );

    if (dbError) {
      console.error('❌ [OTP] DB upsert failed for mobile:', dbError);
      const fallbackKey = `otp_fallback_mobile_${normalizedMobile}`;
      localStorage.setItem(fallbackKey, JSON.stringify({ otp, expiresAt, attempts: 0 }));
    }

    // Send SMS via Edge Function (non-blocking)
    const supabaseUrl = (import.meta.env?.VITE_SUPABASE_URL || '').replace(/\/$/, '');
    const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    const formattedMobile = `+91${normalizedMobile}`;
    const smsMessage = `Your Studyexpo verification code is: ${otp}. Valid for ${OTP_EXPIRY_MINUTES} minutes. Do not share this code.`;

    fetch(`${supabaseUrl}/functions/v1/send-sms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ to: formattedMobile, message: smsMessage, type: 'otp_verification' }),
    }).catch(err => console.warn('⚠️ [OTP] SMS send failed (non-blocking):', err?.message));

    return { success: true, message: 'OTP generated. Check your SMS.' };
  },

  // Verify OTP — checks DB first, falls back to localStorage
  async verifyOTPAsync(identifier, type, enteredOTP) {
    const cleanOTP = String(enteredOTP || '').trim();
    const cleanIdentifier = type === 'email'
      ? identifier.toLowerCase().trim()
      : identifier.replace(/\D/g, '').replace(/^(\+91|91)/, '').slice(-10);

    console.log('🔵 [OTP] Verifying:', { cleanIdentifier, type, cleanOTP });

    // --- Try DB first ---
    const { data: record, error: fetchError } = await supabase
      .from('otp_verifications')
      .select('*')
      .eq('identifier', cleanIdentifier)
      .eq('type', type)
      .single();

    if (!fetchError && record) {
      // Check expiry
      if (new Date(record.expires_at) < new Date()) {
        return { success: false, error: 'OTP has expired. Please request a new one.' };
      }
      // Check attempts
      if (record.attempts >= 5) {
        return { success: false, error: 'Too many failed attempts. Please request a new OTP.' };
      }
      // Check OTP
      if (record.otp_code !== cleanOTP) {
        // Increment attempts
        await supabase
          .from('otp_verifications')
          .update({ attempts: (record.attempts || 0) + 1 })
          .eq('identifier', cleanIdentifier)
          .eq('type', type);
        return { success: false, error: 'Invalid OTP. Please try again.' };
      }
      // Mark as verified
      await supabase
        .from('otp_verifications')
        .update({ verified: true })
        .eq('identifier', cleanIdentifier)
        .eq('type', type);
      console.log('✅ [OTP] Verified via DB');
      return { success: true };
    }

    // --- Fallback: localStorage ---
    const fallbackKey = type === 'email'
      ? `otp_fallback_${cleanIdentifier}`
      : `otp_fallback_mobile_${cleanIdentifier}`;
    const stored = localStorage.getItem(fallbackKey);
    if (stored) {
      try {
        const { otp, expiresAt, attempts } = JSON.parse(stored);
        if (new Date(expiresAt) < new Date()) {
          localStorage.removeItem(fallbackKey);
          return { success: false, error: 'OTP has expired. Please request a new one.' };
        }
        if ((attempts || 0) >= 5) {
          return { success: false, error: 'Too many failed attempts. Please request a new OTP.' };
        }
        if (otp !== cleanOTP) {
          localStorage.setItem(fallbackKey, JSON.stringify({ otp, expiresAt, attempts: (attempts || 0) + 1 }));
          return { success: false, error: 'Invalid OTP. Please try again.' };
        }
        localStorage.removeItem(fallbackKey);
        console.log('✅ [OTP] Verified via localStorage fallback');
        return { success: true };
      } catch {
        localStorage.removeItem(fallbackKey);
      }
    }

    return { success: false, error: 'OTP not found or expired. Please request a new one.' };
  },

  // Synchronous wrapper kept for backward compatibility (now always returns pending)
  verifyOTP(identifier, type, enteredOTP) {
    // This is now async — callers should use verifyOTPAsync
    // Return a pending-like object so old code doesn't crash
    return { success: null, _async: true };
  },
};